import { useState } from "react";
import type { Locale } from "~/utils/locale";
import { t } from "~/utils/translations";
import { getColorHex } from "~/utils/storefront-constants";

/**
 * Aritzia-style desktop filter sidebar.
 *
 * Sticky column fixed to the viewport on product listing pages. Each filter
 * group collapses with a disclosure toggle; selections write to URL search
 * params so the server-side loader handles the actual filtering. Multi-value
 * filters use repeated keys (e.g. `?size=S&size=M`).
 *
 * The sidebar is *controlled* by the parent — callers pass in the current
 * `searchParams` + `onToggle` callback. We don't call `setSearchParams` here
 * because the product grid page is the single source of truth for routing.
 */

export type FilterOption = {
  value: string;
  label?: string;
  count?: number;
  // Two representative hex values shown as overlapping circles in the
  // "swatch-family" rendering. Used by the colour family filter.
  swatches?: [string, string];
};

export interface FilterGroup {
  key: string; // URL param name (e.g. "activity")
  label: string; // Display label
  options: FilterOption[];
  kind?: "checkbox" | "swatch" | "swatch-family" | "pill" | "toggle"; // Rendering style
  defaultOpen?: boolean;
}

interface Props {
  groups: FilterGroup[];
  selected: Record<string, string[]>; // key → array of selected values
  onToggle: (key: string, value: string) => void;
  onClearAll: () => void;
  onClearGroup: (key: string) => void;
  priceMin: string;
  priceMax: string;
  onPriceChange: (min: string, max: string) => void;
  onPriceApply: () => void;
  activeCount: number;
  locale: Locale;
  // Toggle filters (single-value, e.g. New In / On Sale)
  newInActive: boolean;
  onNewInToggle: () => void;
  onSaleActive: boolean;
  onOnSaleToggle: () => void;
  priceFloor?: number;
  priceCeiling?: number;
}

export function FilterSidebar({
  groups,
  selected,
  onToggle,
  onClearAll,
  onClearGroup,
  priceMin,
  priceMax,
  onPriceChange,
  onPriceApply,
  activeCount,
  locale,
  newInActive,
  onNewInToggle,
  onSaleActive,
  onOnSaleToggle,
  priceFloor = 0,
  priceCeiling = 5000000,
}: Props) {
  return (
    <aside className="hidden lg:block w-[240px] flex-shrink-0 sticky top-24 self-start max-h-[calc(100vh-6rem)] overflow-y-auto pr-4 pb-12">
      <div className="flex items-center justify-between mb-5 pb-3 border-b border-black">
        <h2 className="text-xs font-normal uppercase tracking-[0.1em] text-black">
          {t("filters", locale)}
        </h2>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onClearAll}
            className="text-[11px] text-[#767676] underline hover:text-black transition-colors"
          >
            {t("clearAll", locale)}
          </button>
        )}
      </div>

      {/* Toggle filters: New In / On Sale */}
      <div className="space-y-3 pb-5 mb-5 border-b border-[#e0e0e0]">
        <ToggleRow
          label={t("newIn", locale)}
          active={newInActive}
          onToggle={onNewInToggle}
        />
        <ToggleRow
          label={t("onSale", locale)}
          active={onSaleActive}
          onToggle={onOnSaleToggle}
        />
      </div>

      {groups.map((group) => (
        <FilterGroupSection
          key={group.key}
          group={group}
          selected={selected[group.key] || []}
          onToggle={(value) => onToggle(group.key, value)}
          onClear={() => onClearGroup(group.key)}
          locale={locale}
        />
      ))}

      {/* Price range (always last) */}
      <PriceRangeSection
        priceMin={priceMin}
        priceMax={priceMax}
        onChange={onPriceChange}
        onApply={onPriceApply}
        locale={locale}
        floor={priceFloor}
        ceiling={priceCeiling}
      />
    </aside>
  );
}

function ToggleRow({
  label,
  active,
  onToggle,
}: {
  label: string;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <label className="flex items-center justify-between cursor-pointer group">
      <span className="text-xs text-black group-hover:text-[#767676] transition-colors">
        {label}
      </span>
      <button
        type="button"
        onClick={onToggle}
        className={`relative w-9 h-5 rounded-full transition-colors ${
          active ? "bg-black" : "bg-[#e0e0e0]"
        }`}
        aria-pressed={active}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${
            active ? "translate-x-4" : "translate-x-0"
          }`}
        />
      </button>
    </label>
  );
}

function FilterGroupSection({
  group,
  selected,
  onToggle,
  onClear,
  locale,
}: {
  group: FilterGroup;
  selected: string[];
  onToggle: (value: string) => void;
  onClear: () => void;
  locale: Locale;
}) {
  const [open, setOpen] = useState(group.defaultOpen ?? true);
  if (group.options.length === 0) return null;

  // T2.5: wrap each filter group in a <fieldset>/<legend> so screen readers
  // announce the group label ("Size") before each option. WCAG 1.3.1.
  return (
    <fieldset className="pb-5 mb-5 border-b border-[#e0e0e0] border-t-0 border-l-0 border-r-0 p-0 m-0 mt-0 mb-5">
      <legend className="sr-only">{group.label}</legend>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full text-left mb-3 group"
        aria-expanded={open}
      >
        <span className="text-[11px] font-normal uppercase tracking-[0.12em] text-black" aria-hidden="true">
          {group.label}
          {selected.length > 0 && (
            <span className="ml-1 text-[#767676] normal-case tracking-normal">
              ({selected.length})
            </span>
          )}
        </span>
        <svg
          className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div>
          {group.kind === "swatch-family" ? (
            <ul className="space-y-2.5">
              {group.options.map((opt) => {
                const isSelected = selected.includes(opt.value);
                const label = opt.label || opt.value;
                const swatches = opt.swatches ?? [getColorHex(opt.value), getColorHex(opt.value)];
                return (
                  <li key={opt.value}>
                    <label className="flex items-center gap-2.5 cursor-pointer group/opt">
                      <span
                        className={`w-3.5 h-3.5 border flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected ? "bg-black border-black" : "border-[#999] group-hover/opt:border-black"
                        }`}
                      >
                        {isSelected && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={isSelected}
                        onChange={() => onToggle(opt.value)}
                      />
                      <span className="relative inline-flex flex-shrink-0 w-9 h-5" aria-hidden="true">
                        <span
                          className="absolute left-0 top-0 w-5 h-5 rounded-full border border-[#e0e0e0]"
                          style={{ backgroundColor: swatches[0] }}
                        />
                        <span
                          className="absolute right-0 top-0 w-5 h-5 rounded-full border border-[#e0e0e0]"
                          style={{ backgroundColor: swatches[1] }}
                        />
                      </span>
                      <span
                        className={`text-xs flex-1 ${
                          isSelected ? "text-black" : "text-[#555] group-hover/opt:text-black"
                        } transition-colors`}
                      >
                        {label}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          ) : group.kind === "swatch" ? (
            <div className="flex flex-wrap gap-2">
              {group.options.map((opt) => {
                const isSelected = selected.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onToggle(opt.value)}
                    className={`w-7 h-7 rounded-full border-2 transition-all ${
                      isSelected
                        ? "border-black scale-110"
                        : "border-[#e0e0e0] hover:border-[#767676]"
                    }`}
                    style={{ backgroundColor: getColorHex(opt.value) }}
                    title={opt.label || opt.value}
                    aria-label={opt.label || opt.value}
                    aria-pressed={isSelected}
                  />
                );
              })}
            </div>
          ) : group.kind === "pill" ? (
            <div className="flex flex-wrap gap-2">
              {group.options.map((opt) => {
                const isSelected = selected.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onToggle(opt.value)}
                    className={`min-w-[2.5rem] h-9 px-3 text-[11px] border transition-colors ${
                      isSelected
                        ? "border-black bg-black text-white"
                        : "border-[#e0e0e0] text-black hover:border-black"
                    }`}
                  >
                    {opt.label || opt.value}
                  </button>
                );
              })}
            </div>
          ) : (
            <ul className="space-y-2">
              {group.options.map((opt) => {
                const isSelected = selected.includes(opt.value);
                const label = opt.label || formatOptionLabel(opt.value);
                return (
                  <li key={opt.value}>
                    <label className="flex items-center gap-2 cursor-pointer group/opt">
                      <span
                        className={`w-3.5 h-3.5 border flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected ? "bg-black border-black" : "border-[#999] group-hover/opt:border-black"
                        }`}
                      >
                        {isSelected && (
                          <svg
                            className="w-2.5 h-2.5 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={isSelected}
                        onChange={() => onToggle(opt.value)}
                      />
                      <span
                        className={`text-xs flex-1 ${
                          isSelected ? "text-black" : "text-[#555] group-hover/opt:text-black"
                        } transition-colors`}
                      >
                        {label}
                        {typeof opt.count === "number" && (
                          <span className="ml-1 text-[#999]">({opt.count})</span>
                        )}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}

          {selected.length > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="mt-3 text-[10px] uppercase tracking-[0.08em] text-[#767676] underline hover:text-black transition-colors"
            >
              {t("clearAll", locale)}
            </button>
          )}
        </div>
      )}
    </fieldset>
  );
}

function PriceRangeSection({
  priceMin,
  priceMax,
  onChange,
  onApply,
  locale,
  floor,
  ceiling,
}: {
  priceMin: string;
  priceMax: string;
  onChange: (min: string, max: string) => void;
  onApply: () => void;
  locale: Locale;
  floor: number;
  ceiling: number;
}) {
  const [open, setOpen] = useState(true);
  const parsedMin = Number(priceMin);
  const parsedMax = Number(priceMax);
  const min = priceMin && !isNaN(parsedMin) ? Math.max(floor, parsedMin) : floor;
  const max = priceMax && !isNaN(parsedMax) ? Math.min(ceiling, Math.max(min, parsedMax)) : ceiling;
  const range = ceiling - floor || 1;
  const leftPct = ((min - floor) / range) * 100;
  const rightPct = ((ceiling - max) / range) * 100;

  const fmt = (v: number) =>
    v.toLocaleString(locale === "vi" ? "vi-VN" : "en-US");

  return (
    <div className="pb-5 mb-5 border-b border-[#e0e0e0]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full text-left mb-3"
      >
        <span className="text-[11px] font-normal uppercase tracking-[0.12em] text-black">
          {t("priceRange", locale)}
        </span>
        <svg
          className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="space-y-3">
          {/* Labels */}
          <div className="flex items-center justify-between text-[11px] text-[#767676]">
            <span>{fmt(min)}</span>
            <span>{fmt(max)}</span>
          </div>

          {/* Dual-thumb slider */}
          <div className="relative h-5">
            {/* Track background */}
            <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-[2px] bg-[#e0e0e0]" />
            {/* Active range fill */}
            <div
              className="absolute top-1/2 -translate-y-1/2 h-[2px] bg-black"
              style={{ left: `${leftPct}%`, right: `${rightPct}%` }}
            />
            {/* Min thumb */}
            <input
              type="range"
              min={floor}
              max={ceiling}
              step={Math.max(1, Math.round(range / 100))}
              value={min}
              onChange={(e) => {
                const v = Math.min(Number(e.target.value), max);
                onChange(String(v), priceMax || String(ceiling));
              }}
              onMouseUp={onApply}
              onTouchEnd={onApply}
              className="price-range-thumb absolute inset-0 w-full appearance-none bg-transparent pointer-events-none z-[3]"
            />
            {/* Max thumb */}
            <input
              type="range"
              min={floor}
              max={ceiling}
              step={Math.max(1, Math.round(range / 100))}
              value={max}
              onChange={(e) => {
                const v = Math.max(Number(e.target.value), min);
                onChange(priceMin || String(floor), String(v));
              }}
              onMouseUp={onApply}
              onTouchEnd={onApply}
              className="price-range-thumb absolute inset-0 w-full appearance-none bg-transparent pointer-events-none z-[4]"
            />
          </div>

          {/* Manual inputs */}
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              placeholder={t("minPrice", locale)}
              value={priceMin}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || (!isNaN(Number(v)) && Number(v) >= 0)) onChange(v, priceMax);
              }}
              onBlur={onApply}
              className="w-1/2 h-7 px-2 text-[11px] border border-[#e0e0e0] focus:border-black focus:outline-none"
            />
            <span className="text-[10px] text-[#999]">–</span>
            <input
              type="number"
              min="0"
              placeholder={t("maxPrice", locale)}
              value={priceMax}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || (!isNaN(Number(v)) && Number(v) >= 0)) onChange(priceMin, v);
              }}
              onBlur={onApply}
              className="w-1/2 h-7 px-2 text-[11px] border border-[#e0e0e0] focus:border-black focus:outline-none"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function formatOptionLabel(value: string): string {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, " ").toLowerCase();
}
